import { BaseLogo } from "../../core/models/entities/BaseLogo";
import { Design } from "../../core/models/entities/Design";
import {
  CartItem,
  CartItemDesign,
  CartItemLogo,
} from "../../presentation/screens/order-create/models/CartItem";
import { enviroment } from "../../enviroment";
import { parseUtcStamp } from "./storageUpdatedAt";

// "Prices on their way" predicates (flag REACT_APP_PRODUCT_PENDING_ENABLED).
//
// The API stamps BaseLogos.ProductRefreshPendingUtc when a mutation defers
// the supplier product/price look-up to the queue worker and exposes it as
// LogoDto.productPending (DesignDto.pricePending = some placed logo is
// pending). Every UI decision - hide prices, show the badge, lock the
// approve/confirm/add-to-order buttons, start the bounded re-read - goes
// through these predicates, so with the flag off they are ALL false no
// matter what the API sends: no badge element, no disabled attribute, no
// extra call = byte-identical DOM and wire.

export function isLogoProductPending(
  logo: BaseLogo | null | undefined
): boolean {
  if (!enviroment.productPendingEnabled) return false;
  return logo?.productPending === true;
}

// "Preparing logo…" predicates (flag REACT_APP_CLONE_INSTANT_ENABLED).
//
// The API stamps BaseLogos.CloneUploadPendingUtc when a clone is created
// from its source alone and the clone-upload worker still owes it its own
// supplier identity; LogoDto.uploadPending carries it. Unlike a product
// refresh the prices are NOT hidden (the clone carries the source's
// prices) - only the badge, the action locks and the bounded re-read
// apply. Flag off = always false = byte-identical UI.

export type LogoActionLockVariant = "product" | "upload";

export function isLogoUploadPending(
  logo: BaseLogo | null | undefined
): boolean {
  if (!enviroment.cloneInstantEnabled) return false;
  return logo?.uploadPending === true;
}

// "Upload stands alone" predicates (flag REACT_APP_UPLOAD_PENDING_ENABLED,
// design 14/9). The API answers an upload at once when the supplier is down
// or slow - original + row stored, uploadPending stamped with
// uploadPendingKind "upload" - and its worker fetches serial / colours /
// method / high-res later. Such a logo has NO supplier identity yet (0 mm,
// no colours), so the editor locks colour / method / size on top of K3's
// approve / order locks, keeps the user's own file on the canvas and
// re-reads its own GetLogo on the short 2/2/3/3 s plan. A deterministic
// rejection ("Logo size is too big", "Too many artboards", no serial) is
// marked on the row (uploadFailedReason) while the stamp stays - the
// guards hold until the FE deletes the dead row. Flag off = both false =
// byte-identical UI (K3's clone UX applies to any pending upload then).
export function isUploadKindPending(
  logo: BaseLogo | null | undefined
): boolean {
  if (!enviroment.uploadPendingEnabled) return false;
  return isLogoUploadPending(logo) && logo?.uploadPendingKind === "upload";
}

export function isUploadRejected(
  logo: BaseLogo | null | undefined
): boolean {
  if (!enviroment.uploadPendingEnabled) return false;
  return !!logo?.uploadFailedReason;
}

// Every button that ends in a commit / approval / order (the API's guards)
// is locked while EITHER worker still owes the logo something. With both
// flags off this is always false.
export function isLogoActionLocked(
  logo: BaseLogo | null | undefined
): boolean {
  return isLogoProductPending(logo) || isLogoUploadPending(logo);
}

// Which lock tooltip to show: the upload wins when both are pending (no
// supplier identity is the more fundamental reason). undefined = not locked.
export function logoActionLockVariant(
  logo: BaseLogo | null | undefined
): LogoActionLockVariant | undefined {
  if (isLogoUploadPending(logo)) return "upload";
  if (isLogoProductPending(logo)) return "product";
  return undefined;
}

// A design's prices are pending when the API says so (pricePending) OR when
// any of its placed logos is still pending - the API's design.Product is
// rebuilt by the worker seconds later, so an older payload can carry a
// pending logo without the design-level flag.
export function isDesignPricePending(
  design: Design | null | undefined
): boolean {
  if (!enviroment.productPendingEnabled) return false;
  if (!design) return false;
  if (design.pricePending === true) return true;
  return (design.designLogos ?? []).some(
    (dl) => dl?.logo?.productPending === true
  );
}

export function isCartItemProductPending(
  item: CartItem | null | undefined
): boolean {
  if (!enviroment.productPendingEnabled) return false;
  if (!item) return false;
  if (item.type === "Logo") {
    return isLogoProductPending((item as CartItemLogo).baseLogo);
  }
  if (item.type === "Design") {
    const designItem = item as CartItemDesign;
    if (isDesignPricePending(designItem.design)) return true;
    // Instance logos carry their own baseLogo copy (name/number logos placed
    // per size row); cart sync refreshes them together with the design.
    return (designItem.instances ?? []).some((inst) =>
      (inst?.logos ?? []).some((l) => isLogoProductPending(l.baseLogo))
    );
  }
  return false;
}

export function cartItemsProductPending(
  items: CartItem[] | null | undefined
): boolean {
  if (!enviroment.productPendingEnabled) return false;
  return (items ?? []).some(isCartItemProductPending);
}

// Cart lines whose logo (logo line, a design's placed logos or the instance
// rows' copies) is still upload-pending (flag cloneInstantEnabled). Add to
// order is locked while pending, so such a line normally only exists when
// the clone was placed in a design; Continue / Place order stay locked
// until the worker has landed - the API's order guard would answer 409
// otherwise. Flag off = false.
export function isCartItemUploadPending(
  item: CartItem | null | undefined
): boolean {
  if (!enviroment.cloneInstantEnabled) return false;
  if (!item) return false;
  if (item.type === "Logo") {
    return isLogoUploadPending((item as CartItemLogo).baseLogo);
  }
  if (item.type === "Design") {
    const designItem = item as CartItemDesign;
    if (
      (designItem.design?.designLogos ?? []).some(
        (dl) => !dl?.logo?.isDeleted && isLogoUploadPending(dl?.logo)
      )
    ) {
      return true;
    }
    return (designItem.instances ?? []).some((inst) =>
      (inst?.logos ?? []).some((l) => isLogoUploadPending(l.baseLogo))
    );
  }
  return false;
}

export function cartItemsUploadPending(
  items: CartItem[] | null | undefined
): boolean {
  if (!enviroment.cloneInstantEnabled) return false;
  return (items ?? []).some(isCartItemUploadPending);
}

// Continue / Place order lock: prices pending (R5) OR a clone still
// preparing (K3). Both flags off = false.
export function cartItemsActionLocked(
  items: CartItem[] | null | undefined
): boolean {
  return cartItemsProductPending(items) || cartItemsUploadPending(items);
}

export function cartItemsActionLockVariant(
  items: CartItem[] | null | undefined
): LogoActionLockVariant | undefined {
  if (cartItemsUploadPending(items)) return "upload";
  if (cartItemsProductPending(items)) return "product";
  return undefined;
}

// Cart sync (useCartSync) replaces a cart line only when its prices or
// storage differ from the fresh detail read. A worker that lands the SAME
// prices (colour/method change, a size inside the same tier) would then
// leave the line's baseLogo.productPending=true for good: badge on the
// line, Continue/Place order locked, the three polls spent for nothing,
// until F5. Under the flag a change of the pending state is therefore a
// reason to sync on its own. Flag off = both sides are false = no change.
export function cartLogoPendingChanged(
  cartLogo: BaseLogo | null | undefined,
  freshLogo: BaseLogo | null | undefined
): boolean {
  if (!enviroment.productPendingEnabled && !enviroment.cloneInstantEnabled) {
    return false;
  }
  // The clone worker lands the SAME prices (copied from the source), so the
  // upload stamp clearing is the only thing that changes - a reason to sync
  // on its own under cloneInstantEnabled, exactly like the product stamp.
  return (
    isLogoProductPending(cartLogo) !== isLogoProductPending(freshLogo) ||
    isLogoUploadPending(cartLogo) !== isLogoUploadPending(freshLogo)
  );
}

// Same for a design line: the design's own flag / placed logos AND the
// instance rows' baseLogo copies (cart sync refreshes those from the fresh
// designLogos). Deleted logos are ignored on the fresh side because the
// cart filters them out at entry - comparing them would flip on every
// sync and loop through updateItem.
export function cartDesignPendingChanged(
  cartItem: CartItemDesign | null | undefined,
  freshDesign: Design | null | undefined
): boolean {
  if (!enviroment.productPendingEnabled && !enviroment.cloneInstantEnabled) {
    return false;
  }
  if (!cartItem || !freshDesign) return false;
  const liveFreshLogos = (freshDesign.designLogos ?? []).filter(
    (dl) => !dl?.logo?.isDeleted
  );
  const freshFiltered: Design = { ...freshDesign, designLogos: liveFreshLogos };
  if (isDesignPricePending(cartItem.design) !== isDesignPricePending(freshFiltered)) {
    return true;
  }
  if (
    designUploadPending(cartItem.design) !== designUploadPending(freshFiltered)
  ) {
    return true;
  }
  const freshById = new Map(liveFreshLogos.map((dl) => [dl.logoId, dl.logo]));
  return (cartItem.instances ?? []).some((inst) =>
    (inst?.logos ?? []).some((l) => {
      const fresh = freshById.get(l.logoId);
      if (!fresh) return false;
      return (
        isLogoProductPending(l.baseLogo) !== isLogoProductPending(fresh) ||
        isLogoUploadPending(l.baseLogo) !== isLogoUploadPending(fresh)
      );
    })
  );
}

function designUploadPending(design: Design | null | undefined): boolean {
  if (!enviroment.cloneInstantEnabled || !design) return false;
  return (design.designLogos ?? []).some((dl) => isLogoUploadPending(dl?.logo));
}

// Design-list / popup action lock (Approve / Skip / Send for approval / Add
// to order): prices pending (R5) OR a placed clone still preparing (K3).
// Prices are NOT hidden for the upload case - only isDesignPricePending
// drives the price grid. With cloneInstantEnabled off this equals
// isDesignPricePending exactly; both flags off = false.
export function isDesignActionLocked(
  design: Design | null | undefined
): boolean {
  return isDesignPricePending(design) || designUploadPending(design);
}

// Which lock tooltip a design's buttons show: the upload wins when both are
// pending (same rule as logoActionLockVariant). undefined = not locked.
export function designActionLockVariant(
  design: Design | null | undefined
): LogoActionLockVariant | undefined {
  if (designUploadPending(design)) return "upload";
  if (isDesignPricePending(design)) return "product";
  return undefined;
}

// The confirmation page's LOCAL checkout draft (id 0) is built from a frozen
// snapshot of the cart (checkoutItems). When the worker lands a product,
// cart sync patches the LIVE cart only - the snapshot (what the page shows
// AND what Place order posts) would keep the pre-worker baseLogo/design and
// its old or missing prices. This swaps the settled products from the live
// cart into the snapshot's still-pending lines: standalone logo lines by
// logoId (also from a live design that places the same logo), design lines
// by design id (design + its designLogos + the instance rows' copies; deleted
// logos filtered like cart sync). Untouched lines and the array itself keep
// their identity (reference check = "nothing to do"). Flag off = source.
// Known limit: only the PRODUCT stamp is swapped in. A line whose clone is
// still upload-pending (cloneInstantEnabled) would keep its stamp in the
// snapshot - and Place order locked - until the page is left. Unreachable
// in practice: Continue is locked while such a line is in the cart, so the
// snapshot is never taken with one.
export function patchPendingProducts(
  source: CartItem[],
  live: CartItem[]
): CartItem[] {
  if (!enviroment.productPendingEnabled) return source;
  if (!source.length || !live.length) return source;

  const freshLogos = new Map<number, BaseLogo>();
  const freshDesigns = new Map<number, Design>();
  const rememberLogo = (logo: BaseLogo | null | undefined) => {
    if (!logo || isLogoProductPending(logo) || freshLogos.has(logo.id)) return;
    freshLogos.set(logo.id, logo);
  };
  live.forEach((item) => {
    if (!item) return;
    if (item.type === "Logo") {
      rememberLogo((item as CartItemLogo).baseLogo);
    } else if (item.type === "Design") {
      const designItem = item as CartItemDesign;
      const liveLogos = (designItem.design?.designLogos ?? []).filter(
        (dl) => !dl?.logo?.isDeleted
      );
      liveLogos.forEach((dl) => rememberLogo(dl?.logo));
      (designItem.instances ?? []).forEach((inst) =>
        (inst?.logos ?? []).forEach((l) => rememberLogo(l.baseLogo))
      );
      if (
        designItem.design &&
        !isDesignPricePending({ ...designItem.design, designLogos: liveLogos })
      ) {
        freshDesigns.set(designItem.design.id, {
          ...designItem.design,
          designLogos: liveLogos,
        });
      }
    }
  });
  if (freshLogos.size === 0 && freshDesigns.size === 0) return source;

  const swapLogo = <T extends BaseLogo | null | undefined>(logo: T): T => {
    if (!logo || !isLogoProductPending(logo)) return logo;
    return (freshLogos.get(logo.id) as T) ?? logo;
  };

  let changed = false;
  const patched = source.map((item) => {
    if (!item) return item;
    if (item.type === "Logo") {
      const logoItem = item as CartItemLogo;
      const baseLogo = swapLogo(logoItem.baseLogo);
      if (baseLogo === logoItem.baseLogo) return item;
      changed = true;
      return { ...logoItem, baseLogo } as CartItem;
    }
    if (item.type === "Design") {
      const designItem = item as CartItemDesign;
      if (!isCartItemProductPending(designItem)) return item;
      let itemChanged = false;
      let design = designItem.design;
      const fresh = design ? freshDesigns.get(design.id) : undefined;
      if (fresh && isDesignPricePending(design)) {
        design = fresh;
        itemChanged = true;
      } else if (design) {
        const designLogos = (design.designLogos ?? []).map((dl) => {
          const logo = swapLogo(dl.logo);
          if (logo === dl.logo) return dl;
          itemChanged = true;
          return { ...dl, logo };
        });
        if (itemChanged) design = { ...design, designLogos };
      }
      const instances = (designItem.instances ?? []).map((inst) => {
        let instChanged = false;
        const logos = (inst.logos ?? []).map((l) => {
          const baseLogo = swapLogo(l.baseLogo);
          if (baseLogo === l.baseLogo) return l;
          instChanged = true;
          return { ...l, baseLogo };
        });
        if (!instChanged) return inst;
        itemChanged = true;
        return { ...inst, logos };
      });
      if (!itemChanged) return item;
      changed = true;
      return { ...designItem, design, instances } as CartItem;
    }
    return item;
  });
  return changed ? patched : source;
}

// Earliest pending stamp among a design's placed logos - drives the
// "Prices delayed" text for design badges the same way
// productPendingSinceUtc does for a logo. null when nothing is pending or
// the API sent no stamp. Flag off = null.
export function designProductPendingSinceUtc(
  design: Design | null | undefined
): string | null {
  if (!enviroment.productPendingEnabled || !design) return null;
  return earliestStamp(
    (design.designLogos ?? []).map((dl) => dl?.logo)
  );
}

// Earliest pending stamp across the cart's pending lines (logo lines,
// design lines' placed logos and instance-row copies). Flag off = null.
export function cartItemsProductPendingSinceUtc(
  items: CartItem[] | null | undefined
): string | null {
  if (!enviroment.productPendingEnabled) return null;
  const logos: (BaseLogo | null | undefined)[] = [];
  (items ?? []).forEach((item) => {
    if (!item) return;
    if (item.type === "Logo") {
      logos.push((item as CartItemLogo).baseLogo);
    } else if (item.type === "Design") {
      const designItem = item as CartItemDesign;
      (designItem.design?.designLogos ?? []).forEach((dl) =>
        logos.push(dl?.logo)
      );
      (designItem.instances ?? []).forEach((inst) =>
        (inst?.logos ?? []).forEach((l) => logos.push(l.baseLogo))
      );
    }
  });
  return earliestStamp(logos);
}

function earliestStamp(logos: (BaseLogo | null | undefined)[]): string | null {
  let best: { at: number; raw: string } | null = null;
  logos.forEach((logo) => {
    if (!logo || logo.productPending !== true) return;
    const raw = logo.productPendingSinceUtc;
    if (!raw) return;
    const at = parseUtcStamp(raw);
    if (!Number.isFinite(at)) return;
    if (!best || at < best.at) best = { at, raw };
  });
  return best ? (best as { at: number; raw: string }).raw : null;
}

// Age gate for the badge text: after 10 minutes the worker has either
// poisoned (ops mail sent) or the sweep is on it - tell the user it is
// taking longer instead of "in a moment". Pure; `now` is injectable for
// tests. The stamp is read with parseUtcStamp: a GetLogo after the mutation
// carries the EF-read DateTime WITHOUT a zone suffix ("2026-09-10T13:00:00"),
// which Date.parse would take as local time - hours off east of UTC.
export const PRODUCT_PENDING_STALE_AFTER_MS = 10 * 60_000;

export function isProductPendingStale(
  sinceUtc: string | null | undefined,
  now: number = Date.now()
): boolean {
  if (!sinceUtc) return false;
  const since = parseUtcStamp(sinceUtc);
  if (!Number.isFinite(since)) return false;
  return now - since >= PRODUCT_PENDING_STALE_AFTER_MS;
}
